import React from "react";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { FieldArray } from "formik";
import { format, isValid } from "date-fns";

import {
  CustomSelect,
  Input,
  InputGroup,
  AutoSaveField as Field,
  Checkbox,
  DatePicker,
  InlineRadioGroup,
  SelectAutocomplete
} from "../../../../components/Form";
import { DATE_FORMAT } from "../../../../constants";
import { Card } from "../../../../components/v2/Card/Card";
import { Divider, AnotherCitizenshipContainer, MarginB0 } from "./styled";

// Importing nationality-related components and utilities
import { NationalityCheckbox } from "./NationalityCheckbox";
import {
  createAddCitizenshipHandler,
  isAdditionalCitizenshipDisabled,
  getPassportDetailsWithoutId
} from "./utils";
import { MAX_PASSPORT_NUMBER_LENGTH, MAX_ANOTHER_CITIZENSHIP } from "./constants";
import { yesNoOptions } from "../../../../constants/options";

/**
 * Gets filtered nationalities list excluding the current passport's nationality
 *
 * @param {Array} passportDetails - Array of passport details
 * @param {number} currentIndex - Current passport index
 * @returns {Array} - Array of nationalities to filter out
 */
const getFilteredNationalities = (passportDetails, currentIndex) => {
  return passportDetails.flatMap((item, index) =>
    item.nationality && index !== currentIndex ? [item.nationality] : []
  );
};

/**
 * Filters nationality options to exclude already selected nationalities
 *
 * @param {Array} options - List of nationality options
 * @param {Array} passportDetails - Array of passport details
 * @param {number} passportIndex - Current passport index
 * @returns {Array} - Filtered nationality options
 */
const filterNationalityOptions = (options, passportDetails, passportIndex) => {
  const nationalities = getFilteredNationalities(passportDetails, passportIndex);
  return options.filter(item => !nationalities.includes(item.value));
};

/**
 * PersonalInformation component for individual stakeholders
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} - PersonalInformation component
 */
export const PersonalInformation = ({
  index,
  values,
  errors,
  touched,
  setFieldValue,
  createChangeHandler,
  createChangeProspectHandler
}) => {
  const signatoryPath = `prospect.signatoryInfo[${index}]`;

  return (
    <Card title="Personal details" subtitle="This should be the same as shown on his/her passport">
      <Grid item container spacing={3} sx={{ mt: 0 }}>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            pt: "0px !important"
          }}
        >
          <InputGroup
            error={
              (touched.firstName && errors.firstName) || (touched.salutation && errors.salutation)
            }
          >
            <Field
              name="salutation"
              path={`prospect.signatoryInfo[${index}].salutation`}
              component={CustomSelect}
              shrink={false}
              datalistId="salutation"
              inputProps={{ tabIndex: 0 }}
              ErrorMessageComponent={() => null}
            />

            <Field
              name="firstName"
              path={`prospect.signatoryInfo[${index}].firstName`}
              label="First name"
              placeholder="First name"
              component={Input}
              changeProspect={createChangeProspectHandler(values)}
              onKeyUp={event => createChangeHandler(values, setFieldValue, event)}
              InputProps={{
                inputProps: { maxLength: 30, tabIndex: 0 }
              }}
              contextualHelpText="Given Name of the stakeholder exactly the way it is mentioned in the passport"
              ErrorMessageComponent={() => null}
            />
          </InputGroup>
        </Grid>

        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            pt: {
              xs: "16px !important",
              sm: "0px !important"
            }
          }}
        >
          <Field
            name="middleName"
            path={`prospect.signatoryInfo[${index}].middleName`}
            label="Middle Name (optional)"
            placeholder="Middle Name (optional)"
            component={Input}
            onKeyUp={event => createChangeHandler(values, setFieldValue, event)}
            changeProspect={createChangeProspectHandler(values)}
            InputProps={{
              inputProps: { maxLength: 8, tabIndex: 0 }
            }}
          />
        </Grid>
      </Grid>

      <Grid item container spacing={3} sx={{ mt: 0 }}>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            pt: "0px !important"
          }}
        >
          <Field
            name="lastName"
            path={`prospect.signatoryInfo[${index}].lastName`}
            label="Last name"
            placeholder="Last name"
            component={Input}
            onKeyUp={event => createChangeHandler(values, setFieldValue, event)}
            changeProspect={createChangeProspectHandler(values)}
            InputProps={{
              inputProps: { maxLength: 40, tabIndex: 0 }
            }}
            contextualHelpText="Surname of the stakeholder exactly the way it is mentioned in the passport"
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            pt: {
              xs: "16px !important",
              sm: "0px !important"
            }
          }}
        >
          <Field
            name="dateOfBirth"
            path={`prospect.signatoryInfo[${index}].dateOfBirth`}
            label="Date of Birth"
            placeholder="Date of Birth"
            component={DatePicker}
            InputProps={{
              inputProps: { tabIndex: 0 }
            }}
            changeProspect={(_, value, path) =>
              isValid(value) && {
                [path]: format(value, DATE_FORMAT)
              }
            }
            maxDate={new Date()}
            sx={{ my: 0 }}
          />
        </Grid>
      </Grid>
      <Field
        name="isPEP"
        path={`prospect.signatoryInfo[${index}].isPEP`}
        component={InlineRadioGroup}
        options={yesNoOptions}
        label="Does this person, or a relative of this person by blood or by law, or a close associate, hold/has held a position in the government or in a government-owned company/organisation in any country?"
        InputProps={{
          inputProps: { tabIndex: 0 }
        }}
      />
      <FieldArray
        name="passportDetails"
        render={arrayHelper =>
          values.passportDetails.map((item, passportIndex) => {
            const passportDetailsPath = `${signatoryPath}.passportDetails[${passportIndex}]`;

            return (
              <Grid container spacing={3} key={item.id} sx={{ mt: 0 }}>
                {!!passportIndex && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
                <Grid
                  item
                  sm={6}
                  xs={12}
                  sx={{
                    pt: "0px !important"
                  }}
                >
                  <Field
                    name={`passportDetails[${passportIndex}].nationality`}
                    path={`${passportDetailsPath}.nationality`}
                    label="Nationality"
                    component={SelectAutocomplete}
                    datalistId="nationality"
                    filterOptions={options =>
                      filterNationalityOptions(options, values.passportDetails, passportIndex)
                    }
                    shrink={true}
                    tabIndex={2 * passportIndex + 1}
                  />
                </Grid>
                <Grid
                  item
                  sm={6}
                  xs={12}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      sm: "0px !important"
                    }
                  }}
                >
                  <MarginB0>
                    <Field
                      name={`passportDetails[${passportIndex}].passportNumber`}
                      path={`${passportDetailsPath}.passportNumber`}
                      label="Passport number"
                      component={Input}
                      contextualHelpText="If Passport Number contains hyphen (-), oblique (/), spaces or any other special character please enter only alphabets and numbers.
                        Example: 'P-123/1950/456 to be entered as P1231950456'"
                      InputProps={{
                        inputProps: {
                          maxLength: MAX_PASSPORT_NUMBER_LENGTH,
                          tabIndex: 2 * passportIndex + 1
                        }
                      }}
                    />
                  </MarginB0>
                </Grid>

                <Grid
                  item
                  sm={6}
                  xs={12}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      sm: "32px !important"
                    }
                  }}
                >
                  <AnotherCitizenshipContainer>
                    {passportIndex < MAX_ANOTHER_CITIZENSHIP && (
                      <Field
                        name={`passportDetails[${passportIndex}].hasAnotherCitizenship`}
                        path={`${passportDetailsPath}.hasAnotherCitizenship`}
                        label="This person has an extra citizenship"
                        component={NationalityCheckbox}
                        onChange={createAddCitizenshipHandler(arrayHelper, passportIndex)}
                        changeProspect={prospect => ({
                          ...prospect,
                          [`${signatoryPath}.passportDetails`]: getPassportDetailsWithoutId(
                            values.passportDetails
                          )
                        })}
                        disabled={isAdditionalCitizenshipDisabled(values, passportIndex, errors)}
                        inputProps={{ tabIndex: 2 * passportIndex + 2 }}
                      />
                    )}
                  </AnotherCitizenshipContainer>
                </Grid>
                <Grid
                  item
                  sm={6}
                  xs={12}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      sm: "32px !important"
                    }
                  }}
                >
                  <Field
                    name={`passportDetails[${passportIndex}].diplomatPassport`}
                    path={`${passportDetailsPath}.diplomatPassport`}
                    label="This is a diplomatic passport"
                    component={Checkbox}
                    inputProps={{ tabIndex: 2 * passportIndex + 2 }}
                  />
                </Grid>
              </Grid>
            );
          })
        }
      />
    </Card>
  );
};

PersonalInformation.propTypes = {
  index: PropTypes.number.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  createChangeHandler: PropTypes.func.isRequired,
  createChangeProspectHandler: PropTypes.func.isRequired
};
